import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  TrendingUp, Search, ArrowUpRight, ArrowDownRight, AlertCircle,
  Filter, RotateCcw, Globe2, Eye,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import DeepAISignalBadge from "@/components/common/DeepAISignalBadge";

// ---- Filter option lists ----
const SIGNAL_OPTIONS = [
  { value: "all", label: "All signals" },
  { value: "STRONG_BUY", label: "STRONG BUY" },
  { value: "BUY", label: "BUY" },
  { value: "WATCH", label: "WATCH" },
  { value: "HOLD", label: "HOLD" },
  { value: "AVOID", label: "AVOID" },
];

const VALUATION_OPTIONS = [
  { value: "all", label: "All valuations" },
  { value: "Deep Value", label: "Deep Value" },
  { value: "Undervalued", label: "Undervalued" },
  { value: "Fair Value", label: "Fair Value" },
  { value: "Overvalued", label: "Overvalued" },
  { value: "Overpriced", label: "Overpriced" },
];

const COMPANY_TYPE_OPTIONS = [
  { value: "all", label: "All company types" },
  { value: "mega_cap_tech", label: "Mega Cap Tech" },
  { value: "growth", label: "Growth" },
  { value: "dividend_value", label: "Dividend Value" },
  { value: "blend", label: "Blend" },
  { value: "financial", label: "Financial" },
  { value: "reit", label: "REIT" },
];

const MARKET_CAP_OPTIONS = [
  { value: "all", label: "All sizes" },
  { value: "small", label: "Small Cap (< $10B)" },
  { value: "mid", label: "Mid Cap ($10–50B)" },
  { value: "large", label: "Large Cap ($50–200B)" },
  { value: "mega", label: "Mega Cap ($200–500B)" },
  { value: "ultra_mega", label: "Ultra Mega (> $500B)" },
];

const SORT_OPTIONS = [
  { value: "score_desc", label: "Score (high → low)" },
  { value: "score_asc", label: "Score (low → high)" },
  { value: "upside_desc", label: "Upside (high → low)" },
  { value: "yield_desc", label: "Dividend yield (high → low)" },
  { value: "market_cap_desc", label: "Market cap (high → low)" },
  { value: "symbol_asc", label: "Symbol (A → Z)" },
];

// ---- Formatters ----
const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (opts.compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toFixed(2)}`;
};
const fmtNum = (v, d = 1) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);

// ---- Display helpers ----
const SIGNAL_CLASS = {
  STRONG_BUY: "bg-emerald-600 text-white hover:bg-emerald-600",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground",
  AVOID: "bg-destructive/15 text-destructive border border-destructive/30",
};

const SignalBadge = ({ signal }) => {
  if (!signal) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <Badge className={`${SIGNAL_CLASS[signal] || ""} text-xs font-medium`} data-testid={`us-row-signal-${signal}`}>
      {signal.replace("_", " ")}
    </Badge>
  );
};

const VAL_META = {
  "Deep Value":   { cls: "bg-[#14532d] text-white border-[#14532d]",  icon: "💎" },
  "Undervalued":  { cls: "bg-[#16a34a] text-white border-[#16a34a]",  icon: "📉" },
  "Fair Value":   { cls: "bg-[#6b7280] text-white border-[#6b7280]",  icon: "⚖" },
  "Overvalued":   { cls: "bg-[#ea580c] text-white border-[#ea580c]",  icon: "📈" },
  "Overpriced":   { cls: "bg-[#dc2626] text-white border-[#dc2626]",  icon: "🔴" },
};
const ValuationBadge = ({ status }) => {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = VAL_META[status];
  if (!meta) return <Badge variant="outline" className="text-xs">{status}</Badge>;
  return (
    <Badge
      variant="outline"
      data-testid={`us-screener-valuation-${status.toLowerCase().replace(/\s+/g, "-")}`}
      className={`${meta.cls} text-xs font-medium whitespace-nowrap inline-flex items-center gap-1 px-2.5 py-0.5`}
    >
      <span aria-hidden="true" className="leading-none">{meta.icon}</span>
      {status}
    </Badge>
  );
};

const CompanyTypeBadge = ({ type }) => {
  if (!type) return <span className="text-xs text-muted-foreground">—</span>;
  const labelMap = {
    mega_cap_tech: "Mega Tech",
    growth: "Growth",
    dividend_value: "Div Value",
    blend: "Blend",
    financial: "Financial",
    reit: "REIT",
  };
  const cls = {
    mega_cap_tech: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
    growth: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    dividend_value: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    blend: "bg-muted text-muted-foreground",
    financial: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    reit: "bg-pink-500/15 text-pink-700 dark:text-pink-400",
  }[type] || "bg-muted";
  return (
    <Badge variant="outline" className={`${cls} text-[10px] uppercase tracking-wide`}>
      {labelMap[type] || type}
    </Badge>
  );
};

// ---- Phase 8 (Improvement 1) — quality trend arrow ----
const TREND_META = {
  strong_improving: { glyph: "↑↑", cls: "text-emerald-600 dark:text-emerald-400 font-bold", title: "Strong Improving" },
  improving:        { glyph: "↑",  cls: "text-emerald-500", title: "Improving" },
  stable:           { glyph: "→",  cls: "text-muted-foreground", title: "Stable" },
  declining:        { glyph: "↓",  cls: "text-amber-600 dark:text-amber-400", title: "Declining" },
  strong_declining: { glyph: "↓↓", cls: "text-rose-600 dark:text-rose-400 font-bold", title: "Strong Declining" },
};
const QualityTrendBadge = ({ trend }) => {
  const meta = TREND_META[trend];
  if (!meta) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span
      className={`inline-flex items-center justify-center w-6 text-base ${meta.cls}`}
      title={`Quality trend: ${meta.title}`}
      data-testid={`us-trend-${trend}`}
    >
      {meta.glyph}
    </span>
  );
};

// ---- Phase 8 (Improvement 3) — conviction dots ----
// HIGH ●●● green · MEDIUM ●●○ amber · LOW ●○○ grey · SPECULATIVE ○○○ red-tint
const CONVICTION_META = {
  HIGH:        { dots: "●●●", cls: "text-emerald-600 dark:text-emerald-400", title: "High conviction" },
  MEDIUM:      { dots: "●●○", cls: "text-amber-600 dark:text-amber-400",     title: "Medium conviction" },
  LOW:         { dots: "●○○", cls: "text-muted-foreground",                  title: "Low conviction — be cautious" },
  SPECULATIVE: { dots: "○○○", cls: "text-rose-600/70 dark:text-rose-400/70", title: "Speculative — minimal independent confirmation" },
};
const ConvictionDots = ({ label, score }) => {
  const meta = CONVICTION_META[label];
  if (!meta) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-sm ${meta.cls}`}
      title={`${meta.title} (${score == null ? "—" : Number(score).toFixed(1)}/10)`}
      data-testid={`us-conviction-${label}`}
    >
      <span className="tracking-tight">{meta.dots}</span>
      <span className="text-[10px] uppercase tracking-wide">{label === "SPECULATIVE" ? "SPEC" : label}</span>
    </span>
  );
};

// ---- Analyst rating badge ----
// Values arrive normalised by the backend (`recommendation_key` →
// "STRONG BUY" / "BUY" / "NEUTRAL" / "SELL" / "STRONG SELL"). yfinance is
// the source so we label the column "Analyst" rather than "TV" — the
// CSE screener uses TradingView technical signals which we do not get
// from yfinance for US tickers.
const ANALYST_META = {
  "STRONG BUY":  { cls: "bg-[#14532d] text-white border-[#14532d]" },
  "BUY":         { cls: "bg-[#16a34a] text-white border-[#16a34a]" },
  "NEUTRAL":     { cls: "bg-[#6b7280] text-white border-[#6b7280]" },
  "SELL":        { cls: "bg-[#ea580c] text-white border-[#ea580c]" },
  "STRONG SELL": { cls: "bg-[#dc2626] text-white border-[#dc2626]" },
};
const AnalystRatingBadge = ({ rating }) => {
  if (!rating) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = ANALYST_META[rating];
  if (!meta) return <Badge variant="outline" className="text-[10px]">{rating}</Badge>;
  return (
    <Badge
      variant="outline"
      data-testid={`us-analyst-${rating.toLowerCase().replace(/\s+/g, "-")}`}
      title={`Analyst consensus (yfinance) — ${rating}`}
      className={`${meta.cls} text-[10px] font-semibold tracking-wide whitespace-nowrap px-2 py-0.5`}
    >
      {rating}
    </Badge>
  );
};

// ---- Sector verdict badge ----
// Computed server-side by comparing the row's PE to the GICS sector
// median PE from `us_sector_medians`. OVERWEIGHT = sector looks cheap
// relative to peers; UNDERWEIGHT = expensive relative to peers.
const SECTOR_VERDICT_META = {
  OVERWEIGHT:  { short: "OVER",  cls: "bg-[#16a34a] text-white border-[#16a34a]" },
  NEUTRAL:     { short: "NEUT",  cls: "bg-[#6b7280] text-white border-[#6b7280]" },
  UNDERWEIGHT: { short: "UNDER", cls: "bg-[#dc2626] text-white border-[#dc2626]" },
};
const SectorVerdictBadge = ({ verdict }) => {
  if (!verdict) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = SECTOR_VERDICT_META[verdict];
  if (!meta) return <Badge variant="outline" className="text-[10px]">{verdict}</Badge>;
  return (
    <Badge
      variant="outline"
      data-testid={`us-sector-verdict-${verdict.toLowerCase()}`}
      title={`Sector verdict — ${verdict} (PE vs sector median)`}
      className={`${meta.cls} text-[10px] font-bold tracking-wider whitespace-nowrap px-2 py-0.5`}
    >
      {meta.short}
    </Badge>
  );
};

// ---- Page ----
const UsScreenerPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectors, setSectors] = useState([]);

  // Filters (initialised from URL params for shareability)
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sector, setSector] = useState(searchParams.get("sector") || "all");
  const [signal, setSignal] = useState(searchParams.get("signal") || "all");
  const [valuation, setValuation] = useState(searchParams.get("valuation") || "all");
  const [companyType, setCompanyType] = useState(searchParams.get("company_type") || "all");
  const [marketCap, setMarketCap] = useState(searchParams.get("market_cap") || "all");
  const [minScore, setMinScore] = useState(searchParams.get("min_score") || "");
  const [maxScore, setMaxScore] = useState(searchParams.get("max_score") || "");
  const [minYield, setMinYield] = useState(searchParams.get("min_yield") || "");
  const [maxYield, setMaxYield] = useState(searchParams.get("max_yield") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "score_desc");

  // Gating: if non-admin & not flagged, send back to /stocks.
  useEffect(() => {
    if (!user) return;
    if (isAdmin) return;
    if (!user.us_stocks_enabled) {
      toast.error("US Stocks not enabled for this account.");
      navigate("/stocks", { replace: true });
    }
  }, [user, isAdmin, navigate]);

  // Persist last selected market for the switcher
  useEffect(() => {
    try { localStorage.setItem("cse_market_pref", "US"); } catch { /* ignore */ }
  }, []);

  // Sectors load once
  useEffect(() => {
    apiGet("/us-sectors/list")
      .then((d) => setSectors(d?.sectors || []))
      .catch(() => setSectors([]));
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      if (search?.trim()) qs.set("search", search.trim());
      if (sector !== "all") qs.set("sector", sector);
      if (signal !== "all") qs.set("signal", signal);
      if (valuation !== "all") qs.set("valuation", valuation);
      if (companyType !== "all") qs.set("company_type", companyType);
      if (marketCap !== "all") qs.set("market_cap_band", marketCap);
      if (minScore) qs.set("min_score", minScore);
      if (maxScore) qs.set("max_score", maxScore);
      if (minYield) qs.set("min_yield", minYield);
      if (maxYield) qs.set("max_yield", maxYield);
      qs.set("sort", sort);
      qs.set("limit", "600");
      const res = await apiGet(`/us-stocks?${qs.toString()}`);
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load US stocks");
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, sector, signal, valuation, companyType, marketCap, minScore, maxScore, minYield, maxYield, sort]);

  // Debounced reload on filter change. Search is debounced to feel snappy.
  useEffect(() => {
    const t = setTimeout(loadItems, 250);
    return () => clearTimeout(t);
  }, [loadItems]);

  // Mirror filters to URL (lightly debounced via effect)
  useEffect(() => {
    const next = new URLSearchParams();
    if (search?.trim()) next.set("q", search.trim());
    if (sector !== "all") next.set("sector", sector);
    if (signal !== "all") next.set("signal", signal);
    if (valuation !== "all") next.set("valuation", valuation);
    if (companyType !== "all") next.set("company_type", companyType);
    if (marketCap !== "all") next.set("market_cap", marketCap);
    if (minScore) next.set("min_score", minScore);
    if (maxScore) next.set("max_score", maxScore);
    if (minYield) next.set("min_yield", minYield);
    if (maxYield) next.set("max_yield", maxYield);
    if (sort !== "score_desc") next.set("sort", sort);
    setSearchParams(next, { replace: true });
  }, [search, sector, signal, valuation, companyType, marketCap, minScore, maxScore, minYield, maxYield, sort, setSearchParams]);

  const resetFilters = () => {
    setSearch(""); setSector("all"); setSignal("all"); setValuation("all");
    setCompanyType("all"); setMarketCap("all"); setMinScore(""); setMaxScore("");
    setMinYield(""); setMaxYield(""); setSort("score_desc");
  };

  const switchMarket = (market) => {
    try { localStorage.setItem("cse_market_pref", market); } catch { /* ignore */ }
    if (market === "CSE") navigate("/stocks");
  };

  const headlineCounts = useMemo(() => {
    const counts = { STRONG_BUY: 0, BUY: 0, WATCH: 0, HOLD: 0, AVOID: 0 };
    items.forEach((r) => {
      if (r.signal && counts[r.signal] != null) counts[r.signal] += 1;
    });
    return counts;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="us-screener-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Globe2 className="h-6 w-6 text-primary" /> US Stocks Screener
          </h1>
          <p className="text-sm text-muted-foreground">
            Browse {total > 0 ? total : "—"} US stocks scored across our 6-layer composite engine. All prices in USD.
          </p>
        </div>

        {/* Market switcher */}
        <div className="inline-flex items-center rounded-lg border bg-muted/40 p-1" data-testid="us-screener-market-switcher">
          <Button
            size="sm"
            variant="ghost"
            className="px-3 py-1 h-8"
            onClick={() => switchMarket("CSE")}
            data-testid="us-screener-switch-to-cse"
          >
            <span className="mr-1.5">🇱🇰</span> CSE Stocks
          </Button>
          <Button
            size="sm"
            variant="default"
            className="px-3 py-1 h-8"
            data-testid="us-screener-switch-to-us-active"
          >
            <span className="mr-1.5">🇺🇸</span> US Stocks
          </Button>
        </div>
      </div>

      {/* Headline strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="us-screener-headline">
        {Object.entries(headlineCounts).map(([sig, count]) => (
          <Card key={sig} className="px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{sig.replace("_", " ")}</div>
            <div className="text-xl font-semibold tabular-nums">{count}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Filter className="h-4 w-4" /> Filters</span>
            <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="us-screener-reset-filters">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="us-screener-search"
                  className="pl-8"
                  placeholder="Symbol or company name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Sector</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="us-screener-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sectors</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      {s.name} ({s.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Signal</Label>
              <Select value={signal} onValueChange={setSignal}>
                <SelectTrigger data-testid="us-screener-signal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIGNAL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Valuation</Label>
              <Select value={valuation} onValueChange={setValuation}>
                <SelectTrigger data-testid="us-screener-valuation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VALUATION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Company type</Label>
              <Select value={companyType} onValueChange={setCompanyType}>
                <SelectTrigger data-testid="us-screener-company-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Market cap</Label>
              <Select value={marketCap} onValueChange={setMarketCap}>
                <SelectTrigger data-testid="us-screener-market-cap"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MARKET_CAP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Sort by</Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="us-screener-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min score</Label>
                <Input
                  data-testid="us-screener-min-score"
                  type="number" inputMode="decimal" min="0" max="100"
                  placeholder="0" value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max score</Label>
                <Input
                  data-testid="us-screener-max-score"
                  type="number" inputMode="decimal" min="0" max="100"
                  placeholder="100" value={maxScore}
                  onChange={(e) => setMaxScore(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min yield %</Label>
                <Input
                  data-testid="us-screener-min-yield"
                  type="number" inputMode="decimal" min="0"
                  placeholder="0" value={minYield}
                  onChange={(e) => setMinYield(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max yield %</Label>
                <Input
                  data-testid="us-screener-max-yield"
                  type="number" inputMode="decimal" min="0"
                  placeholder="—" value={maxYield}
                  onChange={(e) => setMaxYield(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Results <span className="text-sm font-normal text-muted-foreground">({items.length} of {total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error ? (
            <div className="p-8 flex items-center gap-3 text-sm">
              <AlertCircle className="h-5 w-5 text-destructive" /> {error}
            </div>
          ) : loading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No US stocks match your filters.{" "}
              <button className="underline" onClick={resetFilters}>Clear filters</button>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">PE</TableHead>
                    <TableHead className="text-right">Yield</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-center" title="Quality / fundamentals trend (Phase 8)">Trend</TableHead>
                    <TableHead>Signal</TableHead>
                    <TableHead title="Conviction = how many independent signals agree (Phase 8)">Conviction</TableHead>
                    <TableHead>Valuation</TableHead>
                    <TableHead>Deep AI</TableHead>
                    <TableHead>Analyst</TableHead>
                    <TableHead>Sector Verdict</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Upside</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => {
                    const upClass =
                      r.upside_pct > 0 ? "text-emerald-600 dark:text-emerald-400" :
                      r.upside_pct < 0 ? "text-destructive" : "text-muted-foreground";
                    const UpsideArrow = r.upside_pct > 0 ? ArrowUpRight : ArrowDownRight;
                    return (
                      <TableRow
                        key={r.symbol}
                        className="hover:bg-muted/40 cursor-pointer"
                        onClick={() => navigate(`/us-stocks/${encodeURIComponent(r.symbol)}`)}
                        data-testid={`us-screener-row-${r.symbol}`}
                      >
                        <TableCell>
                          <Link
                            to={`/us-stocks/${encodeURIComponent(r.symbol)}`}
                            className="font-mono font-semibold text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.symbol}
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-[14rem] truncate text-sm">{r.company_name || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.sector || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(r.latest_close)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNum(r.pe_ratio)}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.dividend_yield_pct == null ? "—" : fmtPct(r.dividend_yield_pct)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{r.composite_score?.toFixed(1) ?? "—"}</TableCell>
                        <TableCell className="text-center"><QualityTrendBadge trend={r.quality_trend} /></TableCell>
                        <TableCell><SignalBadge signal={r.signal} /></TableCell>
                        <TableCell><ConvictionDots label={r.conviction_label} score={r.conviction_score} /></TableCell>
                        <TableCell><ValuationBadge status={r.valuation_status} /></TableCell>
                        <TableCell>
                          {r.has_deep_analysis && r.deep_ai_verdict ? (
                            <Link
                              to={`/us-stocks/${encodeURIComponent(r.symbol)}?tab=deep-ai`}
                              data-testid={`us-screener-deep-ai-${r.symbol}`}
                              title={`${r.symbol} Deep AI — ${r.deep_ai_verdict}\nConviction: ${r.deep_ai_conviction || "—"}\nGenerated: ${r.deep_ai_generated_at ? new Date(r.deep_ai_generated_at).toLocaleDateString() : "—"}\nClick to view full analysis`}
                              className="inline-block hover:opacity-80 transition-opacity"
                            >
                              <DeepAISignalBadge
                                signal={r.deep_ai_verdict}
                                isPremium
                                generatedAt={r.deep_ai_generated_at}
                              />
                            </Link>
                          ) : (
                            <span className="text-xs text-muted-foreground" data-testid={`us-screener-deep-ai-empty-${r.symbol}`}>—</span>
                          )}
                        </TableCell>
                        <TableCell><AnalystRatingBadge rating={r.analyst_rating} /></TableCell>
                        <TableCell><SectorVerdictBadge verdict={r.sector_verdict} /></TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(r.base_target_price)}</TableCell>
                        <TableCell className={`text-right tabular-nums whitespace-nowrap ${upClass}`}>
                          {r.upside_pct == null ? "—" : (
                            <span className="inline-flex items-center gap-0.5">
                              <UpsideArrow className="h-3 w-3" />
                              {Math.abs(r.upside_pct).toFixed(1)}%
                            </span>
                          )}
                        </TableCell>
                        <TableCell><CompanyTypeBadge type={r.company_type} /></TableCell>
                        <TableCell>
                          <Button
                            size="sm" variant="ghost"
                            onClick={(e) => { e.stopPropagation(); navigate(`/us-stocks/${encodeURIComponent(r.symbol)}`); }}
                            data-testid={`us-screener-view-${r.symbol}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />
      <div className="text-xs text-muted-foreground">
        Data via <span className="font-mono">yfinance</span>. Updated daily after US market close.
        Composite scoring reflects our 6-layer engine with company-type-aware valuation methods.
      </div>
    </div>
  );
};

export default UsScreenerPage;
